import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import WebsiteLayout from "../layouts/WebsiteLayout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import BackNavButton from "../components/common/BackNavButton";
import FavoriteAppUnlockButton from "../components/common/FavoriteAppUnlockButton";
import { vibesColors as colors } from "../theme/vibesColors";
import { buildHomeNewPayload, postHomeNew } from "../services/apiService";
import { extractNearbyHostsForCards } from "../utils/nearbyHostsHelpers";
import { getSavedHomeLocation } from "../utils/homeLocationStorage";
import "./HomePage.css";

function getStoredUserId() {
  const keys = ["userId", "userid", "_id", "id"];
  const stores = [window.localStorage, window.sessionStorage];
  for (const store of stores) {
    for (const key of keys) {
      const direct = store.getItem(key);
      if (direct) return direct;
    }
    const userRaw = store.getItem("user");
    if (userRaw) {
      try {
        const user = JSON.parse(userRaw);
        const value = user?.userId || user?._id || user?.id || "";
        if (value) return String(value);
      } catch {
        // ignore
      }
    }
  }
  return "";
}

export default function NearbyHostsPage() {
  const location = useLocation();
  const [listing, setListing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const requestFilters = useMemo(() => {
    const fromNav = location.state?.filters;
    if (fromNav && typeof fromNav === "object") {
      return buildHomeNewPayload(fromNav);
    }
    const savedLocation = getSavedHomeLocation();
    return buildHomeNewPayload({
      lat: savedLocation.lat,
      long: savedLocation.long,
      userId: getStoredUserId(),
      categoryid: "",
      search_keyword: "",
    });
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await postHomeNew(requestFilters);
        if (cancelled) return;
        setListing(extractNearbyHostsForCards(res));
      } catch (e) {
        if (!cancelled) {
          setListing([]);
          setError(e instanceof Error ? e.message : "Failed to load nearby hosts");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestFilters]);

  return (
    <WebsiteLayout>
      <section className="home-page-wrap">
        <div className="home-page-inner">
          <div className="nearby-hosts-top">
            <BackNavButton to="/" ariaLabel="Back to home" />
            <h1 className="nearby-hosts-title">Nearby Events</h1>
            <span className="nearby-hosts-top-spacer" aria-hidden />
          </div>

          {error ? <ErrorMessage message={error} /> : null}

          {loading ? (
            <LoadingSpinner label="Loading..." />
          ) : (
            <div className="home-page-list">
              {listing.length === 0 ? (
                <p className="home-page-empty-text">No nearby hosts found.</p>
              ) : (
                listing.map((item) => (
                  <article key={item.id} className="home-page-card">
                    <div className="home-page-card-image-wrap">
                      {item.image ? (
                        <img src={item.image} alt={item.title} className="home-page-card-image" />
                      ) : (
                        <div className="home-page-card-fallback" />
                      )}
                    </div>
                    <div className="home-page-card-content">
                      <h3>{item.title}</h3>
                      {item.address ? <p className="home-page-card-address">{item.address}</p> : null}
                      {item.description ? <p className="home-page-card-description">{item.description}</p> : null}
                      {item.distance ? <p className="home-page-card-distance">{item.distance}</p> : null}
                    </div>
                    <div className="home-page-card-actions">
                      <FavoriteAppUnlockButton ariaLabel={`Favorite ${item.title}`} />
                      <Link
                        to={item.id ? `/club/${encodeURIComponent(item.id)}` : "/"}
                        state={{ fromHome: true }}
                        className="home-page-card-action"
                        style={{ background: colors.BtnBackground, color: colors.white }}
                        aria-label={`Open ${item.title}`}
                      >
                        →
                      </Link>
                    </div>
                  </article>
                ))
              )}
            </div>
          )}
        </div>
      </section>
    </WebsiteLayout>
  );
}
